import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CalendarBlank, Clock, CheckCircle, Spinner, CurrencyDollar } from "@phosphor-icons/react";
import { API } from "@/lib/site";

export const Booking = () => {
  const [config, setConfig] = useState(null);
  const [sessionType, setSessionType] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [wlOpen, setWlOpen] = useState(false);
  const [wlForm, setWlForm] = useState({ name: "", email: "", note: "" });
  const [wlBusy, setWlBusy] = useState(false);
  const [wlDone, setWlDone] = useState(false);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!wlForm.name || !wlForm.email) return toast.error("Add your name and email.");
    setWlBusy(true);
    try {
      const res = await fetch(`${API}/booking/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...wlForm, session_type: sessionType, preferred_date: selectedDate }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setWlDone(true);
      toast.success(d.message || "You're on the waitlist.");
    } catch {
      toast.error("Could not join the waitlist. Please try again.");
    } finally {
      setWlBusy(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/booking/config`)
      .then((r) => r.json())
      .then((d) => {
        setConfig(d);
        if (d.session_types?.length) selectType(d.session_types[0].id);
      })
      .catch(() => toast.error("Could not load booking options."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectType = (id) => {
    setSessionType(id);
    setSelectedDate(null);
    setSelectedSlot(null);
    setConfirmed(null);
    setLoadingAvail(true);
    fetch(`${API}/booking/availability?session_type=${id}`)
      .then((r) => r.json())
      .then((d) => {
        setAvail(d);
        if (d.availability?.length) setSelectedDate(d.availability[0].date);
      })
      .catch(() => toast.error("Could not load availability."))
      .finally(() => setLoadingAvail(false));
  };

  const activeDay = avail?.availability?.find((d) => d.date === selectedDate);
  const activeType = config?.session_types?.find((s) => s.id === sessionType);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return toast.error("Please pick a time slot.");
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/booking/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_type: sessionType,
          date: selectedDate,
          start_time: selectedSlot.start,
          name: form.name,
          email: form.email,
          notes: form.notes,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail;
        const msg = typeof detail === "string" ? detail : "Could not book. Please try again.";
        throw new Error(msg);
      }
      if (data.paid && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmed(data.booking);
      toast.success("Your discovery call is booked!");
    } catch (err) {
      toast.error(err.message || "Could not book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-brand-line py-3 text-brand-text placeholder:text-brand-dim/50 focus:outline-none focus:border-brand-gold transition-colors";

  return (
    <section id="booking" className="relative py-24 lg:py-32 bg-brand-surface2/40 border-y border-brand-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="overline">Work with me 1:1</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-brand-text mt-4 max-w-2xl">
              Book a coaching call.
            </h2>
          </div>
          {config && (
            <p className="text-brand-dim max-w-sm">
              All times shown in{" "}
              <span className="text-brand-gold">{config.timezone_label}</span>. Choose a session,
              pick a time, and you're set.
            </p>
          )}
        </div>

        {confirmed ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="booking-confirmed"
            className="rounded-3xl border border-brand-line bg-brand-surface p-10 lg:p-16 text-center max-w-2xl mx-auto"
          >
            <CheckCircle size={56} weight="fill" className="text-brand-sage mx-auto" />
            <h3 className="font-serif text-3xl text-brand-text mt-6">You're booked in.</h3>
            <p className="text-brand-dim mt-3">
              {confirmed.session_name} on{" "}
              <span className="text-brand-text">{confirmed.date}</span> at{" "}
              <span className="text-brand-text">{confirmed.start_time}</span> ({confirmed.tz_label}).
              A confirmation is on its way to your inbox.
            </p>
            <button
              data-testid="booking-again"
              onClick={() => selectType(sessionType)}
              className="mt-8 rounded-full border border-brand-line px-6 py-3 text-sm text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              Book another
            </button>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Session type + form */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-3">
                {config?.session_types?.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`session-type-${s.id}`}
                    onClick={() => selectType(s.id)}
                    className={`w-full text-left rounded-2xl border p-5 transition-colors duration-300 ${
                      sessionType === s.id
                        ? "border-brand-gold bg-brand-surface"
                        : "border-brand-line hover:border-brand-sage/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-xl text-brand-text">{s.name}</span>
                      <span className="text-sm font-medium text-brand-gold flex items-center gap-1">
                        {s.price > 0 ? (
                          <>
                            <CurrencyDollar size={14} />
                            {s.price.toFixed(0)}
                          </>
                        ) : (
                          "Free"
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-brand-dim mt-1">{s.desc}</p>
                    <p className="text-xs text-brand-dim/70 mt-2 flex items-center gap-1">
                      <Clock size={12} /> {s.duration} min
                    </p>
                  </button>
                ))}
              </div>

              <form onSubmit={submit} data-testid="booking-form" className="rounded-2xl border border-brand-line bg-brand-surface p-6 space-y-5">
                <div>
                  <label className="overline block mb-2">Name</label>
                  <input
                    data-testid="booking-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className={field}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input
                    data-testid="booking-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com"
                    className={field}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Anything I should know?</label>
                  <textarea
                    data-testid="booking-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    placeholder="Optional"
                    className={field + " resize-none"}
                  />
                </div>
                <div className="pt-2 text-sm text-brand-dim">
                  {selectedSlot ? (
                    <span data-testid="booking-selection-summary">
                      {activeDay?.day} · {selectedSlot.label} · {activeType?.duration} min
                    </span>
                  ) : (
                    "Select a date and time →"
                  )}
                </div>
                <button
                  data-testid="booking-submit"
                  type="submit"
                  disabled={submitting || !selectedSlot}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Spinner size={18} className="animate-spin" /> Booking
                    </>
                  ) : activeType?.price > 0 ? (
                    `Continue to payment · $${activeType.price.toFixed(0)}`
                  ) : (
                    "Confirm free call"
                  )}
                </button>
              </form>
            </div>

            {/* Calendar */}
            <div className="lg:col-span-7 rounded-3xl border border-brand-line bg-brand-surface p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-6 text-brand-text">
                <CalendarBlank size={22} className="text-brand-gold" />
                <span className="font-serif text-2xl">Pick a date</span>
              </div>

              {loadingAvail ? (
                <div className="flex items-center gap-2 text-brand-dim py-12 justify-center">
                  <Spinner size={20} className="animate-spin" /> Loading availability…
                </div>
              ) : !avail?.availability?.length ? (
                <p className="text-brand-dim py-12 text-center">
                  No open slots right now — please check back soon or send a message.
                </p>
              ) : (
                <>
                  <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1">
                    {avail.availability.map((d) => (
                      <button
                        key={d.date}
                        data-testid={`booking-date-${d.date}`}
                        onClick={() => {
                          setSelectedDate(d.date);
                          setSelectedSlot(null);
                        }}
                        className={`flex-shrink-0 w-20 rounded-2xl border px-3 py-4 text-center transition-colors ${
                          selectedDate === d.date
                            ? "border-brand-gold bg-brand-gold/10"
                            : "border-brand-line hover:border-brand-sage/50"
                        }`}
                      >
                        <span className="overline block text-brand-dim">{d.weekday}</span>
                        <span className="block font-serif text-lg text-brand-text mt-1">
                          {d.day.split(" ")[1]}
                        </span>
                        <span className="text-[10px] text-brand-dim">{d.day.split(" ")[0]}</span>
                      </button>
                    ))}
                  </div>

                  <div className="hairline my-6" />

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedDate}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="grid grid-cols-3 sm:grid-cols-4 gap-3"
                    >
                      {activeDay?.slots.map((s) => (
                        <button
                          key={s.start}
                          data-testid={`booking-slot-${s.start}`}
                          onClick={() => setSelectedSlot(s)}
                          className={`rounded-xl border py-3 text-sm transition-colors ${
                            selectedSlot?.start === s.start
                              ? "border-brand-gold bg-brand-gold text-brand-bg font-medium"
                              : "border-brand-line text-brand-text hover:border-brand-gold"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                </>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 rounded-3xl border border-brand-line bg-brand-surface p-6 lg:p-8" data-testid="waitlist-block">
          {wlDone ? (
            <p className="text-brand-text" data-testid="waitlist-done">You're on the waitlist — Tony's team will reach out the moment an evening slot opens. 🌙</p>
          ) : !wlOpen ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-brand-dim">Evenings all booked? Join the waitlist and I'll let you know when a spot frees up.</p>
              <button data-testid="waitlist-open" onClick={() => setWlOpen(true)} className="rounded-full border border-brand-gold px-6 py-3 text-sm font-medium text-brand-gold hover:bg-brand-gold hover:text-brand-bg transition-colors whitespace-nowrap">
                Join the waitlist
              </button>
            </div>
          ) : (
            <form onSubmit={submitWaitlist} data-testid="waitlist-form" className="grid sm:grid-cols-3 gap-4 items-end">
              <input data-testid="waitlist-name" value={wlForm.name} onChange={(e) => setWlForm({ ...wlForm, name: e.target.value })} placeholder="Your name" className="bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              <input data-testid="waitlist-email" type="email" value={wlForm.email} onChange={(e) => setWlForm({ ...wlForm, email: e.target.value })} placeholder="you@email.com" className="bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              <input data-testid="waitlist-note" value={wlForm.note} onChange={(e) => setWlForm({ ...wlForm, note: e.target.value })} placeholder="Preferred days/times (optional)" className="bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              <button data-testid="waitlist-submit" type="submit" disabled={wlBusy} className="sm:col-span-3 justify-self-start rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60">
                {wlBusy ? "Joining…" : "Add me to the waitlist"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
